// Percentage-mode estimates use SKY assignment types and their published weights.
export function gradeNumber(value) {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

// SKY category percentages are formatted strings (for example "99.30%").
// Keep the score/weight parser strict; only published percentages accept a % suffix.
export function publishedPercentage(value) {
  return gradeNumber(typeof value === 'string' ? value.trim().replace(/%$/, '').trim() : value);
}

export function assignmentCategory(assignment) {
  const name = assignment.assignment_type?.name || assignment.type;
  return typeof name === 'string' && name.trim() && name.trim() !== 'Unknown' ? name.trim() : 'Uncategorized';
}

export function includedAssignments(assignments) {
  return assignments.filter(assignment => !assignment.exempt && !assignment.formative);
}

export function publishedWeights(assignments) {
  const weights = {};
  for (const assignment of includedAssignments(assignments)) {
    const category = assignmentCategory(assignment);
    const weight = gradeNumber(assignment.assignment_type?.weight);
    if (category === 'Uncategorized' || weight === null || (Object.hasOwn(weights, category) && weights[category] !== weight)) return null;
    weights[category] = weight;
  }
  return Object.values(weights).some(weight => weight > 0) ? weights : null;
}

export function postedGrades(assignments) {
  return includedAssignments(assignments).flatMap(assignment => {
    const score = gradeNumber(assignment.assignment_percentage);
    const category = assignmentCategory(assignment);
    return score !== null ? [{ score, category: category && category !== 'Unknown' ? category : 'Uncategorized' }] : [];
  });
}

export function estimateGrade(grades, weights = null) {
  if (!grades.length) return null;
  if (!weights) return grades.reduce((sum, grade) => sum + grade.score, 0) / grades.length;
  let total = 0;
  let totalWeight = 0;
  for (const [category, rawWeight] of Object.entries(weights)) {
    const weight = gradeNumber(rawWeight);
    if (weight === null) return null;
    const scores = grades.filter(grade => grade.category === category);
    if (!scores.length || weight === 0) continue;
    total += scores.reduce((sum, grade) => sum + grade.score, 0) / scores.length * weight;
    totalWeight += weight;
  }
  return totalWeight > 0 ? total / totalWeight : null;
}

// Blackbaud reports category averages to two decimals.
const REPORTED_TOLERANCE = 0.006;
const MAX_SUBSETS = 5000;

function* subsets(items, size, start = 0, chosen = []) {
  if (chosen.length === size) { yield chosen; return; }
  for (let i = start; i <= items.length - (size - chosen.length); i++) yield* subsets(items, size, i + 1, [...chosen, items[i]]);
}

// SKY doesn't expose "exclude from cumulative grade" or drop-lowest rules, so
// infer them: find the fewest exclusions whose mean matches the reported average.
// Returns null when nothing matches (assignment factors, points grading, etc.).
export function inferCounted(scored, reported) {
  const mean = list => list.reduce((sum, item) => sum + item.score, 0) / list.length;
  if (reported === null || !scored.length || Math.abs(mean(scored) - reported) < REPORTED_TOLERANCE) return { count: scored.length, excluded: [] };
  let checked = 0;
  for (let size = scored.length - 1; size > 0; size--) {
    const matches = [];
    for (const subset of subsets(scored, size)) {
      if (++checked > MAX_SUBSETS) return null;
      if (Math.abs(mean(subset) - reported) < REPORTED_TOLERANCE) matches.push(new Set(subset));
    }
    if (!matches.length) continue;
    // Only name assignments that are excluded in every matching subset.
    const excluded = scored.filter(item => matches.every(match => !match.has(item))).map(item => item.assignment);
    return { count: size, excluded };
  }
  return null;
}

// Prefer Blackbaud's category average; new assignments count as one
// equal-weight item among the assignments that Blackbaud actually counts.
export function categorySummaries(assignments) {
  const groups = new Map();
  for (const assignment of assignments) {
    const name = assignmentCategory(assignment);
    if (!groups.has(name)) groups.set(name, { name, assignments: [] });
    groups.get(name).assignments.push(assignment);
  }
  return [...groups.values()].map(group => {
    const included = includedAssignments(group.assignments);
    const scored = included.flatMap(assignment => {
      const score = gradeNumber(assignment.assignment_percentage);
      return score !== null ? [{ assignment, score }] : [];
    });
    const percentages = included.map(item => publishedPercentage(item.assignment_type?.percentage)).filter(value => value !== null);
    const reported = percentages.length && percentages.every(value => value === percentages[0]) ? percentages[0] : null;
    const weights = included.map(item => gradeNumber(item.assignment_type?.weight));
    const weight = weights.length && weights.every(value => value !== null && value === weights[0]) ? weights[0] : null;
    const counted = inferCounted(scored, reported);
    return {
      ...group,
      count: counted?.count ?? scored.length,
      excluded: counted?.excluded ?? [],
      reported,
      weight,
      average: reported ?? estimateGrade(scored),
    };
  });
}

export function estimateScenario(assignments, additions, weights = null) {
  if (additions.some(row => gradeNumber(row.score) === null)) return null;
  const groups = categorySummaries(assignments).filter(group => group.count > 0);
  if (!groups.length || additions.some(row => !groups.some(group => group.name === row.category))) return null;
  let baseline = 0;
  let projection = 0;
  let totalWeight = 0;
  for (const group of groups) {
    const extra = additions.filter(row => row.category === group.name);
    const average = (group.average * group.count + extra.reduce((sum, row) => sum + gradeNumber(row.score), 0)) / (group.count + extra.length);
    if (weights) {
      const weight = gradeNumber(weights[group.name]);
      if (weight === null) return null;
      baseline += group.average * weight;
      projection += average * weight;
      totalWeight += weight;
    } else {
      baseline += group.average * group.count;
      projection += average * (group.count + extra.length);
      totalWeight += group.count;
    }
  }
  if (totalWeight === 0) return null;
  return { baseline: baseline / totalWeight, projection: projection / (weights ? totalWeight : totalWeight + additions.length) };
}
